import { useEffect, useRef } from "react";
import type { LocalHost } from "@picnic-field/local-host/protocol";
import type { PrincipleScreenLabel } from "@picnic-field/renderer/principle";
import { PRINCIPLE_COLORS } from "@picnic-field/renderer/principle";
import { Button } from "../../../components/ui/Button";
import { useI18n } from "../../../i18n";
import type { PrincipleState, PrincipleViewLike } from "../types";

/**
 * P2-S5 원리 보기(SR-PHY-07 · FS-03 · FS-04) — 셸 쪽 조각.
 *  - 토글: 켜면 호스트(C-05 setPrinciple — 커널이 StateExport principle 을 싣는다)와 뷰(C-02 setPrinciple — 화살표 오버레이)를 함께 켠다.
 *  - SE = 화살표만(수치 라벨 없음) · SU = 수치 라벨(rpm · N·m · N) — 밀도에서 정한다.
 *  - 범례: 색 = 무엇(기어 회전 · 구동 토크 · 접촉 반력 · 무게 · 무게중심). SE 는 쉬운 말.
 *  - 수치 라벨: 뷰가 페인트할 때 화면 좌표를 다시 쓴 풀 배열 → 같은 span 을 다시 쓴다(프레임마다 새 요소를 만들지 않는다).
 */

/** 호스트 원리 보기를 토글 상태에 맞춘다(새 load 뒤에도 다시 건다) */
export function useHostPrinciple(host: () => LocalHost | null, on: boolean, loadKey: unknown) {
  useEffect(() => {
    host()?.setPrinciple?.(on);
    // host 는 ref 접근자(매 렌더 새 함수일 수 있다) — 토글 · load 가 바뀔 때만
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, loadKey]);
}

/** 뷰에 토글 상태를 건다 — 뷰가 준비되거나 상태가 바뀔 때 */
export function applyViewPrinciple(v: PrincipleViewLike | null, p: PrincipleState | undefined) {
  v?.setPrinciple?.({ on: p?.on ?? false, numbers: p?.numbers ?? false });
}

const KIND_COLOR: Readonly<Record<PrincipleScreenLabel["kind"], string>> = {
  joint: PRINCIPLE_COLORS.joint,
  torque: PRINCIPLE_COLORS.torque,
  force: PRINCIPLE_COLORS.force,
  weight: PRINCIPLE_COLORS.weight,
};

/** 페인트마다 수치 라벨 자리 — 풀 배열 · span 재사용 */
export function usePrincipleLabels() {
  const layer = useRef<HTMLDivElement>(null);
  const pool = useRef<PrincipleScreenLabel[]>([]);
  const place = (v: PrincipleViewLike | null) => {
    const el = layer.current;
    if (!el) return;
    const n = v?.principleLabels ? v.principleLabels(pool.current) : 0;
    while (el.childElementCount < n) {
      const s = document.createElement("span");
      s.className = "pf-principle__label";
      el.appendChild(s);
    }
    for (let i = 0; i < el.childElementCount; i++) {
      const s = el.children[i] as HTMLSpanElement;
      const l = pool.current[i];
      if (i >= n || !l) {
        if (s.style.display !== "none") s.style.display = "none";
        continue;
      }
      s.style.display = "";
      if (s.textContent !== l.text) s.textContent = l.text;
      if (s.dataset.kind !== l.kind) {
        s.dataset.kind = l.kind;
        // 테두리 = 화살표 색(범례와 같다)
        s.style.borderColor = KIND_COLOR[l.kind];
      }
      s.style.transform = `translate(${Math.round(l.x)}px, ${Math.round(l.y)}px) translate(-50%, -120%)`;
    }
  };
  return { layer, place };
}

/** 라벨 층(장면 위 · 누를 수 없음) */
export function PrincipleLabelLayer({
  layer,
  on,
}: {
  layer: React.RefObject<HTMLDivElement>;
  on: boolean;
}) {
  return (
    <div
      ref={layer}
      className="pf-principle__labels"
      hidden={!on}
      aria-hidden="true"
      data-testid="principle-labels"
    />
  );
}

/** 토글 버튼(HUD · 조작 줄) */
export function PrincipleButton({
  on,
  onToggle,
  hud,
}: {
  on: boolean;
  onToggle: () => void;
  /** HUD 칩 모양(전시관 오른쪽 위) — 아니면 조작 줄 버튼 */
  hud?: boolean;
}) {
  const { t } = useI18n();
  if (hud)
    return (
      <button
        type="button"
        className={`pf-hud pf-gallery__hudbtn pf-principle__toggle${on ? " pf-principle__toggle--on" : ""}`}
        aria-pressed={on}
        onClick={onToggle}
        data-testid="hud-principle"
      >
        <span aria-hidden="true">💡</span> {t("course.principle.toggle")}
      </button>
    );
  return (
    <Button
      variant="ghost"
      icon="💡"
      className={`pf-principle__toggle${on ? " pf-principle__toggle--on" : ""}`}
      aria-pressed={on}
      onClick={onToggle}
      data-testid="ctl-principle"
    >
      {t("course.principle.toggle")}
    </Button>
  );
}

const LEGEND = [
  { key: "gear", color: PRINCIPLE_COLORS.joint, mark: "↻" },
  { key: "torque", color: PRINCIPLE_COLORS.torque, mark: "↻" },
  { key: "force", color: PRINCIPLE_COLORS.force, mark: "↑" },
  { key: "weight", color: PRINCIPLE_COLORS.weight, mark: "↓" },
  { key: "com", color: PRINCIPLE_COLORS.com, mark: "●" },
] as const;

/** 범례 — 색 = 무엇(SE 쉬운 말 · SU 용어) */
export function PrincipleLegend({ se }: { se: boolean }) {
  const { t } = useI18n();
  return (
    <ul
      className="pf-hud pf-principle__legend"
      aria-label={t("course.principle.legend")}
      data-testid="principle-legend"
    >
      {LEGEND.map((l) => (
        <li key={l.key}>
          <span className="pf-principle__swatch" style={{ color: l.color }} aria-hidden="true">
            {l.mark}
          </span>
          {t(`course.principle.${l.key}${se ? "SE" : "SU"}`)}
        </li>
      ))}
    </ul>
  );
}
